import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'

export interface JobPosition {
  id: string
  name: string
  sortOrder: number
  createdAt: string
  userCount: number
}

export interface CreateJobPositionInput {
  name: string
  sortOrder?: number
}

export interface UpdateJobPositionInput {
  name?: string
  sortOrder?: number
}

const POSITIONS_KEY = ['admin', 'job-positions'] as const

export function useJobPositions() {
  return useQuery({
    queryKey: POSITIONS_KEY,
    queryFn: () =>
      apiFetch<{ positions: JobPosition[] }>('/api/admin/job-positions').then(
        (r) => r.positions,
      ),
    staleTime: 60_000,
  })
}

export function useCreateJobPosition() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateJobPositionInput) =>
      apiFetch<{ position: JobPosition }>('/api/admin/job-positions', {
        method: 'POST',
        body: { ...input },
      }).then((r) => r.position),
    onSuccess: () => qc.invalidateQueries({ queryKey: POSITIONS_KEY }),
  })
}

export function useUpdateJobPosition() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateJobPositionInput }) =>
      apiFetch<{ position: JobPosition }>(`/api/admin/job-positions/${id}`, {
        method: 'PATCH',
        body: { ...input },
      }).then((r) => r.position),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: POSITIONS_KEY })
      qc.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })
}

export function useDeleteJobPosition() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<null>(`/api/admin/job-positions/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: POSITIONS_KEY })
      qc.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })
}
